import React, { useEffect, useState, useMemo } from "react";
import { api, fmtUSD, API, getToken } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Download, Search, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  High: "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Medium-High": "bg-lime-100 text-lime-800 border-lime-300",
  Medium: "bg-amber-100 text-amber-800 border-amber-300",
  Low: "bg-red-100 text-red-800 border-red-300",
  "No-Data": "bg-slate-100 text-slate-600 border-slate-300",
};

const SUPER_THEME = {
  Procedure: { ring: "ring-blue-200", bg: "from-blue-50", text: "text-blue-900" },
  Personnel: { ring: "ring-violet-200", bg: "from-violet-50", text: "text-violet-900" },
  Site: { ring: "ring-amber-200", bg: "from-amber-50", text: "text-amber-900" },
};

/**
 * StudyAnalysis — given a CTA id, renders the Stryde Budget Analysis layout:
 * 3 summary KPI cards (Procedure/Personnel/Site) + 3 detail tables.
 *
 * Props:
 *   ctaId         (string)   — direct mode
 *   ctas          (array)    — picker mode (shows a Select to pick a CTA)
 *   showPicker    (bool)     — default true if ctas provided
 *   embedded      (bool)     — true = no outer card padding (used inside CTA detail)
 */
export default function StudyAnalysis({ ctaId, ctas, showPicker = true, embedded = false }) {
  const [pickerId, setPickerId] = useState(ctaId || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ctaId) setPickerId(ctaId);
  }, [ctaId]);

  useEffect(() => {
    if (!pickerId) { setData(null); return; }
    setLoading(true);
    api.get(`/ctas/${pickerId}/study-analysis`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [pickerId]);

  const exportXlsx = async () => {
    if (!pickerId) return;
    try {
      const token = getToken();
      const r = await fetch(`${API}/ctas/${pickerId}/study-analysis.xlsx`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`Export failed (${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data?.cta?.protocol_number || "study"}_analysis.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  return (
    <div className={embedded ? "" : "space-y-5"} data-testid="study-analysis">
      {showPicker && ctas && (
        <div className="flex flex-wrap items-end gap-3 bg-white border border-border rounded-md p-4 shadow-sm">
          <div className="flex-1 min-w-[260px]">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Pick a CTA to analyze</div>
            <Select value={pickerId} onValueChange={setPickerId}>
              <SelectTrigger data-testid="study-analysis-picker"><SelectValue placeholder="Select a study…" /></SelectTrigger>
              <SelectContent>
                {ctas.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.protocol_number || "—"} · {c.study_name?.slice(0, 60) || "(no name)"} · {c.sponsor || ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {data && (
            <Button onClick={exportXlsx} variant="outline" data-testid="study-analysis-export">
              <Download className="w-4 h-4 mr-1" /> Export Excel
            </Button>
          )}
        </div>
      )}

      {!showPicker && data && (
        <div className="flex justify-end">
          <Button onClick={exportXlsx} variant="outline" size="sm" data-testid="study-analysis-export">
            <Download className="w-3.5 h-3.5 mr-1" /> Export Excel
          </Button>
        </div>
      )}

      {loading && <div className="text-slate-500 text-sm">Analyzing…</div>}

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.summary.map((s) => (
              <SummaryCard key={s.super_category} s={s} />
            ))}
          </div>

          {/* Meta strip */}
          <div className="flex flex-wrap gap-3 text-xs text-slate-500">
            <span><strong className="text-slate-700">{data.meta.n_matched}</strong> of {data.meta.n_total_items} items matched to Rate Card</span>
            <span>·</span>
            <span>Rate card size: <strong className="text-slate-700">{data.meta.ratecard_size}</strong> canonical items</span>
            {data.meta.n_unmatched > 0 && (
              <>
                <span>·</span>
                <span className="text-amber-700"><strong>{data.meta.n_unmatched}</strong> unmatched (no benchmark)</span>
              </>
            )}
          </div>

          {/* Detail sections */}
          <DetailSection title="Procedure Fees" rows={data.details.Procedure || []} super_category="Procedure" />
          <DetailSection title="Personnel Fees" rows={data.details.Personnel || []} super_category="Personnel" />
          <DetailSection title="Site Fees" rows={data.details.Site || []} super_category="Site" />
          {(data.details["Pass-through"] || []).length > 0 && (
            <DetailSection title="Pass-throughs" rows={data.details["Pass-through"]} super_category="Pass-through" />
          )}
          {(data.details.Unmatched || []).length > 0 && (
            <DetailSection title="Unmatched (no benchmark in Rate Card)" rows={data.details.Unmatched} super_category="Unknown" />
          )}
        </>
      )}

      {!loading && pickerId && !data && (
        <div className="text-slate-500 text-sm">No analysis available.</div>
      )}
    </div>
  );
}

function SummaryCard({ s }) {
  const theme = SUPER_THEME[s.super_category] || SUPER_THEME.Procedure;
  const variance = s.variance_vs_p75 || 0;
  const positive = variance >= 0;
  return (
    <div
      className={`bg-gradient-to-br ${theme.bg} to-white border border-border ring-1 ${theme.ring} rounded-md p-5 shadow-sm`}
      data-testid={`summary-${s.super_category.toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <div className={`text-xs uppercase tracking-wider font-semibold ${theme.text}`}>{s.super_category}</div>
        <Badge variant="outline" className="text-[10px]">{s.matched}/{s.items} matched</Badge>
      </div>
      <div className="font-mono-fin text-2xl font-bold text-slate-900 mt-2">{fmtUSD(s.matched_negotiated || 0)}</div>
      <div className="text-[11px] text-slate-500">Negotiated (matched-only)</div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        <Stat label="P75" value={s.p75} />
        <Stat label="P80" value={s.p80} />
        <Stat label="P90" value={s.p90} />
      </div>

      <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between">
        <div className="text-[11px] text-slate-500">Variance vs P75</div>
        <div className={`flex items-center gap-1 font-mono-fin font-semibold ${positive ? "text-emerald-700" : "text-red-700"}`}>
          {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
          {fmtUSD(variance)}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="font-mono-fin text-sm font-semibold">{fmtUSD(value || 0)}</div>
    </div>
  );
}

function DetailSection({ title, rows, super_category }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q || q.length < 2) return rows;
    const ql = q.toLowerCase();
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(ql) ||
      (r.canonical || "").toLowerCase().includes(ql) ||
      (r.status || "").toLowerCase().includes(ql)
    );
  }, [rows, q]);
  if (!rows.length) return null;

  const sub_neg = rows.reduce((s, r) => s + (r.negotiated || 0), 0);
  const sub_p75 = rows.filter((r) => r.matched).reduce((s, r) => s + (r.p75 || 0), 0);
  const sub_p80 = rows.filter((r) => r.matched).reduce((s, r) => s + (r.p80 || 0), 0);
  const sub_p90 = rows.filter((r) => r.matched).reduce((s, r) => s + (r.p90 || 0), 0);

  return (
    <div className="bg-white border border-border rounded-md shadow-sm overflow-auto" data-testid={`detail-${super_category.toLowerCase()}`}>
      <div className="px-5 py-3 border-b border-border flex items-center gap-3">
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <Badge variant="outline" className="text-[10px]">{rows.length} items</Badge>
        <div className="flex-1" />
        <div className="relative w-56">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9 h-8 text-xs"
            placeholder="Filter…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>
      <table className="w-full text-sm stryde-table">
        <thead>
          <tr>
            <th className="text-left px-4 py-2">Cost Item</th>
            <th className="text-right px-4 py-2">Negotiated</th>
            <th className="text-center px-4 py-2 w-[110px]">Status</th>
            <th className="text-right px-4 py-2">P75</th>
            <th className="text-right px-4 py-2">P80</th>
            <th className="text-right px-4 py-2">P90</th>
            <th className="text-left px-4 py-2 hidden lg:table-cell">Analysis</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && (
            <tr><td colSpan={7} className="text-center py-6 text-slate-500">No matches.</td></tr>
          )}
          {filtered.map((r, i) => (
            <tr key={`${r.canonical}-${i}`} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-4 py-2">
                <div className="font-medium text-slate-900">{r.name}</div>
                {r.canonical && r.canonical.toLowerCase() !== (r.name || "").toLowerCase() && (
                  <div className="text-[10px] text-slate-400">≈ {r.canonical}</div>
                )}
              </td>
              <td className="px-4 py-2 text-right font-mono-fin font-semibold">{fmtUSD(r.negotiated)}</td>
              <td className="px-4 py-2 text-center">
                <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[r.status] || STATUS_STYLE["No-Data"]}`}>
                  {r.status}
                </Badge>
              </td>
              <td className="px-4 py-2 text-right font-mono-fin text-slate-700">{r.p75 != null ? fmtUSD(r.p75) : "—"}</td>
              <td className="px-4 py-2 text-right font-mono-fin text-slate-700">{r.p80 != null ? fmtUSD(r.p80) : "—"}</td>
              <td className="px-4 py-2 text-right font-mono-fin text-emerald-700 font-semibold">{r.p90 != null ? fmtUSD(r.p90) : "—"}</td>
              <td className="px-4 py-2 hidden lg:table-cell text-xs text-slate-500 max-w-md truncate" title={r.analysis_note || ""}>
                {r.analysis_note || ""}
              </td>
            </tr>
          ))}
          {/* Subtotal */}
          <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
            <td className="px-4 py-2 text-slate-900">Subtotal</td>
            <td className="px-4 py-2 text-right font-mono-fin">{fmtUSD(sub_neg)}</td>
            <td className="px-4 py-2"></td>
            <td className="px-4 py-2 text-right font-mono-fin text-[#0A4C95]">{fmtUSD(sub_p75)}</td>
            <td className="px-4 py-2 text-right font-mono-fin text-[#0A4C95]">{fmtUSD(sub_p80)}</td>
            <td className="px-4 py-2 text-right font-mono-fin text-emerald-700">{fmtUSD(sub_p90)}</td>
            <td className="px-4 py-2"></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
